import React, { useState, useMemo } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  useToast,
  Icon,
  Badge,
  Button,
  Collapse,
} from '@chakra-ui/react';
import { CreatableSelect } from 'chakra-react-select';
import { FiTag } from 'react-icons/fi';
import { applyTags } from '../../utils/crmTags';
import { useAuth } from '../../contexts/AuthContext';
import { useCrmSettings } from '../../contexts/CrmSettingsContext';
import { useTagRemovalConfirm } from './ConfirmTagRemovalDialog';

/**
 * Tag editor for a single contact or deal.
 *
 * Contact and deal tags are one set: writes go to the contact and the database
 * mirrors them onto the contact's deals. Passing `dealId` marks the deal as the
 * one the user acted on, so deal-scoped TAG_ADDED/TAG_REMOVED campaigns still
 * enroll exactly as they did when deals had their own tag list.
 */
export default function TagSelector({
  dealId,
  contactId,
  currentTags = [],
  userId,
  isEditable = true,
  onTagsChange,
}) {
  const [loading, setLoading] = useState(false);
  const [showSelector, setShowSelector] = useState(false);
  const toast = useToast();

  const { currentUser, SuperAdmin, Roles } = useAuth();
  const isCRMAdmin = SuperAdmin || Roles?.includes('CRM Admin');
  const { getVisibleTags, getTag, tags: allTags, refreshTags } = useCrmSettings();
  const { confirmTagRemoval, dialog: removalDialog } = useTagRemovalConfirm();

  const actorId = userId || currentUser?.uid;

  const visibleTags = useMemo(
    () => getVisibleTags(actorId, isCRMAdmin) || [],
    [actorId, isCRMAdmin, getVisibleTags]
  );

  const availableTags = useMemo(() => {
    return visibleTags
      .filter(tag => !currentTags.includes(tag.value))
      .map(t => ({ value: t.value, label: t.label, color: t.color || 'gray' }));
  }, [visibleTags, currentTags]);

  const getTagColor = (tagValue) => {
    const tag = allTags.find(t => t.value === tagValue);
    return tag ? (tag.color || 'gray') : 'gray';
  };

  const getTagLabel = (tagValue) => {
    const tag = getTag(tagValue);
    return tag ? tag.label : tagValue;
  };

  const writeTags = async ({ add = [], remove = [] }) => {
    if (!dealId && !contactId) return false;

    setLoading(true);
    try {
      const nextTags = await applyTags({ contactId, dealId, add, remove, actorId });

      if (onTagsChange) {
        onTagsChange(nextTags || currentTags);
      }

      const added = add.length > 0;
      toast({
        title: added ? 'Tag added' : 'Tag removed',
        description: dealId && contactId
          ? `Tag ${added ? 'added to' : 'removed from'} deal and contact`
          : `Tag ${added ? 'added' : 'removed'}`,
        status: 'success',
        duration: 2000,
      });
      return true;
    } catch (error) {
      console.error('Error updating tags:', error);
      toast({
        title: add.length > 0 ? 'Error adding tag' : 'Error removing tag',
        description: error.message,
        status: 'error',
        duration: 3000,
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = async (option) => {
    if (!option?.value) return;
    const ok = await writeTags({ add: [option.value] });
    // A brand-new tag was just registered server-side by crm_tag_apply — pull
    // the registry now so it shows up in every dropdown in this tab
    // immediately instead of waiting on the realtime round-trip.
    if (ok && option.__isNew__) {
      refreshTags();
    }
    setShowSelector(false);
  };

  const handleRemoveTag = async (tagValue) => {
    // An unlinked deal (contact_id is null) keeps its own tags, so the prompt
    // must not claim a contact is affected when there isn't one.
    const affectsContact = Boolean(contactId);

    const confirmed = await confirmTagRemoval({
      title: 'Remove this tag?',
      tags: [getTagLabel(tagValue)],
      description: dealId && affectsContact
        ? 'This removes the tag from this deal and its contact.'
        : dealId
          ? 'This removes the tag from this deal.'
          : 'This removes the tag from this contact.',
      note: affectsContact
        ? "Tags are shared, so it is also removed from this contact's other deals."
        : undefined,
      confirmLabel: 'Remove tag',
    });

    if (!confirmed) return;

    await writeTags({ remove: [tagValue] });
  };

  return (
    <VStack align="stretch" spacing={3}>
      <Box>
        <HStack spacing={2} mb={2} align="center" justify="space-between">
          <HStack spacing={2}>
            <Icon as={FiTag} boxSize={4} />
            <Text fontSize="xs" fontWeight="medium" textTransform="uppercase" color="gray.600">
              Tags
            </Text>
            {currentTags.length > 0 && (
              <Badge size="xs" colorScheme="blue">
                {currentTags.length}
              </Badge>
            )}
          </HStack>
          {isEditable && (
            <Button
              size="xs"
              leftIcon={<Icon as={FiTag} boxSize={3} />}
              onClick={() => setShowSelector(!showSelector)}
              variant="outline"
              colorScheme="blue"
            >
              Add Tag
            </Button>
          )}
        </HStack>

        <Wrap>
          {currentTags.length > 0 ? (
            currentTags.map((tagValue) => (
              <WrapItem key={tagValue}>
                <Tag
                  size="sm"
                  colorScheme={getTagColor(tagValue)}
                  borderRadius="full"
                >
                  <TagLabel>{getTagLabel(tagValue)}</TagLabel>
                  {isEditable && (
                    <TagCloseButton
                      onClick={() => handleRemoveTag(tagValue)}
                      isDisabled={loading}
                    />
                  )}
                </Tag>
              </WrapItem>
            ))
          ) : (
            <Text fontSize="sm" color="gray.500">
              No tags
            </Text>
          )}
        </Wrap>
      </Box>

      {isEditable && (
        <Collapse in={showSelector} animateOpacity>
          <Box>
            <CreatableSelect
              placeholder="Type to search or create..."
              options={availableTags}
              onChange={handleAddTag}
              value={null}
              isClearable={false}
              size="sm"
              isDisabled={loading}
              formatCreateLabel={(inputValue) => `Create "${inputValue}"`}
              autoFocus
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              chakraStyles={{
                control: (provided) => ({
                  ...provided,
                  fontSize: 'sm',
                }),
                menu: (provided) => ({
                  ...provided,
                  fontSize: 'sm',
                  zIndex: 9999,
                }),
                menuPortal: (provided) => ({
                  ...provided,
                  zIndex: 9999,
                }),
              }}
            />
            <Text fontSize="2xs" color="gray.500" mt={1}>
              Type to search existing tags or create a new one.
            </Text>
          </Box>
        </Collapse>
      )}

      {removalDialog}
    </VStack>
  );
}
